import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import { useAlgoAcctInfo, useBalance } from "@/hooks/use-algo-account";
import { useHistoryEvents } from "@/hooks/use-history-events";
import { formatNumber, N, shortenAmount } from "@/lib/utils";
import { DSContractState } from "@myth-finance/dualstake-ts-sdk";
import { useWallet } from "@txnlab/use-wallet";
import { cx } from "class-variance-authority";
import { AlertTriangleIcon, CornerDownRight, User } from "lucide-react";
import { useMemo } from "react";
import { Row } from "./activity-card";
import { HelpButton } from "@/components/help-button";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// realized/unrealized calculations algo
//
// algo_stake=0
// asa_spent=0
// asa_worth=computed => algo_stake * rate
// unrealized=0
// realized=0

// for each mint m_algo, m_asa
//   -> derive rate from algo/asa
//   algo_stake += m_algo
//   asa_spent += m_asa
//   unrealized=asa_worth - asa_spent

// for each redeem w_algo, w_asa
//   -> derive rate from algo/asa
//   deposit_rate = asa_spent / algo_stake
//   withdraw_spent_equiv = w_algo * deposit_rate
//   realized += w_asa - withdraw_spent_equiv
//   algo_stake -= w_algo
//   asa_spent -= withdraw_spent_equiv

interface ProfitState {
  algoStakeAfter: number;
  asaSpentAfter: number;
  realized: number;
  valid: boolean;
}

interface ProfitEvent extends ProfitState {
  type: "mint" | "redeem";

  eventAlgoAmount: number;
  eventAsaAmount: number;

  // algoStakeAfter: number;
  // asaSpentAfter: number;
  // // asaWorth
  unrealized: number;
  // realized
}

export function YourStakeCard({
  cState,
  className,
}: {
  className?: string;
  cState: DSContractState;
}) {
  const {
    lstId,
    asaId,
    asaDecimals,
    asaUnitName,
    appId,
    lstName,
    rate,
    ratePrecision,
  } = cState;
  const { activeAddress } = useWallet();
  const {
    data: userAcct,
    isPending: userIsPending,
    error: userError,
    refetch: userRefetch,
  } = useAlgoAcctInfo(activeAddress);
  const userLstBalance = useBalance(lstId, userAcct);

  const {
    error,
    refetch,
    data: appEvents,
    isPending,
  } = useHistoryEvents(appId);
  const textColors = getHighlightClasses(asaId, "text");
  const borderColors = getHighlightClasses(asaId, "border");

  const profitEvents = useMemo(() => {
    if (!activeAddress) return [];
    // console.log(
    //   "parsing appEvents",
    //   activeAddress?.slice(0, 8),
    //   appEvents?.events.length,
    //   appEvents?.round
    // );
    const mrs =
      appEvents?.events.filter(
        ({ spec: { name }, outerTransaction: { sender } }) =>
          (name === "mint" || name === "redeem") && sender === activeAddress
      ) ?? [];
    const state: ProfitState = {
      algoStakeAfter: 0,
      asaSpentAfter: 0,
      realized: 0,
      valid: true,
    };
    const outEvents: ProfitEvent[] = [];
    for (const mr of mrs.reverse()) {
      if (mr.spec.name === "mint") {
        const eventAlgoAmount = Number(mr.argsByName.algo_amount);
        const eventAsaAmount = Number(mr.argsByName.asa_amount_received);

        const rate = eventAsaAmount / eventAlgoAmount;

        state.algoStakeAfter += eventAlgoAmount;
        state.asaSpentAfter += eventAsaAmount;

        const asaWorth = state.algoStakeAfter * rate;
        const unrealized = asaWorth - state.asaSpentAfter;

        if (state.asaSpentAfter < 0) state.valid = false;

        outEvents.push({
          type: "mint",
          ...state,
          eventAlgoAmount,
          eventAsaAmount,
          unrealized,
        });
      } else if (mr.spec.name === "redeem") {
        const eventAlgoAmount = Number(mr.argsByName.algo_amount);
        const eventAsaAmount = Number(mr.argsByName.asa_amount);

        const spendRate = state.asaSpentAfter / state.algoStakeAfter;
        const withdrawRate = eventAsaAmount / eventAlgoAmount;

        const eventAsaEquivalentSpend = eventAlgoAmount * spendRate;
        state.realized += eventAsaAmount - eventAsaEquivalentSpend;
        state.algoStakeAfter -= eventAlgoAmount;
        state.asaSpentAfter -= eventAsaEquivalentSpend;

        const asaWorth = state.algoStakeAfter * withdrawRate;
        const unrealized = asaWorth - state.asaSpentAfter;

        // redeem
        outEvents.push({
          type: "redeem",
          ...state,
          eventAlgoAmount,
          eventAsaAmount,
          unrealized,
        });
      } else {
        throw new Error("Unexpected event spec " + JSON.stringify(mr.spec));
      }
    }
    return outEvents;
  }, [activeAddress, appEvents?.round]);

  const lastProfitEvent = profitEvents?.length
    ? profitEvents[profitEvents.length - 1]
    : undefined;

  const pairedLstAsaWorth =
    userLstBalance >= 0 ? (N(rate) * userLstBalance) / N(ratePrecision) : 0;

  const mintedLstAsaWorth =
    lastProfitEvent && lastProfitEvent?.algoStakeAfter
      ? (N(rate) * lastProfitEvent?.algoStakeAfter) / N(ratePrecision)
      : 0;

  const unrealizedW =
    lastProfitEvent && lastProfitEvent?.algoStakeAfter
      ? Math.min(mintedLstAsaWorth, pairedLstAsaWorth) -
        lastProfitEvent.asaSpentAfter
      : 0;

  const realizedW = lastProfitEvent ? lastProfitEvent.realized : 0;

  const showProfitValues = profitEvents.length === 0 || lastProfitEvent?.valid;

  const unrealizedIsPartial =
    lastProfitEvent && lastProfitEvent.algoStakeAfter < userLstBalance
      ? userLstBalance - lastProfitEvent.algoStakeAfter
      : 0;

  return unrealizedW || realizedW || userLstBalance > 0 ? (
    <Card className={cx("min-w-[300px] relative self-stretch", className)}>
      <CardHeader>
        <CardTitle className="opacity-0 animate-fade-in-2 relative">
          <User className={`relative -top-[2px] inline mr-[2px] ${textColors}`} />
          <span className={textColors}>Your {lstName}</span>
          <HelpButton
            side="top"
            sideOffset={0}
            large={true}
            triggerClassName="absolute top-0 right-0"
            title={<>{lstName} performance legend</>}
          >
            <div>This panel shows your {lstName} peformance.</div>
            <div className="flex flex-col">
              <div>
                <span className={textColors}>Holding</span> is your current{" "}
                {lstName} balance.
              </div>
              <div>
                <span className={textColors}>
                  <CornerDownRight size={16} className="inline -mt-2 mr-1" />
                  Paired
                </span>{" "}
                is the amount of {asaUnitName} contained in your {lstName}.
              </div>
            </div>

            <div>
              <span className={textColors}>Unrealized</span> is the{" "}
              {asaUnitName} appreciation of your <u>minted</u> {lstName}{" "}
              holdings.{" "}
              <span className="text-muted-foreground">
                {cState.lstName} received from other wallets is not included.
              </span>
            </div>

            <div>
              <span className={textColors}>Realized</span> are withdrawn{" "}
              {asaUnitName} gains.{" "}
              <span className="text-muted-foreground">
                It is the sum of {cState.asaUnitName} appreciation of your {cState.lstName} between your
                mints and your redemptions.
              </span>{" "}
            </div>

            <div className={`border-l-2 mt-2 pl-2 ${borderColors}`}>
              <strong className={textColors}>Warning</strong> this requires all
              mints and redeems to happen through the same wallet.{" "}
              <span className={textColors}>
                Results will be partial or inaccurate
              </span>{" "}
              if you have sent or received {lstName} between wallets.
            </div>
            
            <div className={`border-l-2 -mt-2 pl-2 ${borderColors}`}>
              <strong className={textColors}>Heads up</strong> This feature is
              in beta.
            </div>
          </HelpButton>
        </CardTitle>
      </CardHeader>
      <CardContent className="opacity-0 animate-fade-in-2 flex flex-col">
        <WithErrorAndLoading
          mini={true}
          loading={userIsPending}
          error={userError?.message}
          retry={userRefetch}
        >
          <Row label="Holding" labelClass="text-muted-foreground">
            {userLstBalance > 0 ? formatNumber(userLstBalance, 6, 2) : "0"}{" "}
            {lstName}
          </Row>
          <Row
            labelClass="text-muted-foreground"
            label={
              <>
                <CornerDownRight size={16} className="inline -mt-2 mr-1" />
                Paired
              </>
            }
          >
            {shortenAmount(pairedLstAsaWorth, asaDecimals)} {asaUnitName}
          </Row>

          <WithErrorAndLoading
            mini={true}
            loading={isPending}
            error={error?.message}
            retry={refetch}
          >
            <Row
              className="mt-2"
              labelClass="text-muted-foreground"
              label="Unrealized"
              innerClass={
                unrealizedW <= 1
                  ? ""
                  : "flex justify-end items-center text-green-500"
              }
            >
              {showProfitValues ? (
                <>
                  {unrealizedIsPartial && unrealizedW > 1 ? (
                    <PartialResults
                      minted={lastProfitEvent?.algoStakeAfter ?? 0}
                      cState={cState}
                      additional={unrealizedIsPartial}
                    />
                  ) : null}
                  {unrealizedW <= 1
                    ? "0"
                    : "+" + shortenAmount(unrealizedW, asaDecimals)}
                  {""} {asaUnitName}
                </>
              ) : (
                <NA cState={cState} />
              )}
            </Row>

            <Row
              className=""
              labelClass="text-muted-foreground"
              label="Realized"
              innerClass={realizedW > 0 ? "text-green-500" : ""}
            >
              {showProfitValues ? (
                <>
                  {realizedW > 0
                    ? "+" + shortenAmount(realizedW, asaDecimals)
                    : 0}{" "}
                  {asaUnitName}
                </>
              ) : (
                <NA cState={cState} />
              )}
            </Row>
          </WithErrorAndLoading>
        </WithErrorAndLoading>
      </CardContent>
      <User
        size={192}
        strokeWidth="1.25px"
        className="absolute pointer-events-none z-0 top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] opacity-5"
      />
    </Card>
  ) : null;
}

function NA({ cState }: { cState: DSContractState }) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger className="text-muted-foreground underline">
          N/A
        </TooltipTrigger>
        <TooltipContent side="bottom">
          Performance can not be calculated accurately:
          <br />
          You have redeemed more {cState.lstName} than you have minted.
          <br />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function PartialResults({
  cState,
  additional,
  minted,
}: {
  cState: DSContractState;
  additional: number;
  minted: number;
}) {
  if (additional < 10000) return;
  return (
    <HelpButton
      title="Warning: Partial calculation"
      side="bottom"
      trigger={(size, className) => (
        <AlertTriangleIcon
          className={cx("inline mr-1 text-orange-500 opacity-100", className)}
          size={size}
        />
      )}
      infoIcon={(size, className) => (
        <AlertTriangleIcon className={className} size={size} />
      )}
    >
      <div>
        Unrealized {cState.asaUnitName} profit covers only the amount you have{" "}
        <u>minted directly</u>: {formatNumber(minted, 6, 2)} {cState.lstName}
        <br />
        <br />
        You have an additional {formatNumber(additional, 6, 2)} {cState.lstName}{" "}
        non-minted balance, which is not included as it has unknown cost basis.
      </div>
    </HelpButton>
  );
}
